import ThemeModal from "components/Common/ThemeModal"
import { Badge, Button, Col, Row, Spinner, Table } from "reactstrap"
import { formatReadableDate } from "utils/formatDate"
import { formatCurrency } from "utils/formatCurrency"
import { getEntityLabel, getUserLabel } from "pages/AccountPages/utils/displayValue"

const getMoneyDisplayValue = (value, currency = "BDT") => formatCurrency(value || 0, currency)

const ReceiptRow = ({ label, value, mono = false, strong = false }) => (
  <div className="d-flex align-items-start justify-content-between gap-3 py-2" style={{ borderBottom: "1px dashed rgba(15, 23, 42, 0.12)" }}>
    <span className="text-muted text-uppercase fw-semibold" style={{ fontSize: 10, letterSpacing: 1 }}>{label}</span>
    <span className={`${mono ? "font-monospace" : ""} ${strong ? "fw-bold" : "fw-semibold"} text-dark text-end`.trim()} style={{ fontSize: strong ? 15 : 14 }}>
      {value || "-"}
    </span>
  </div>
)

const ReceiptSection = ({ title, children }) => (
  <div className="pt-3">
    <div className="text-uppercase text-muted fw-semibold mb-2" style={{ fontSize: 11, letterSpacing: 1 }}>{title}</div>
    {children}
  </div>
)

const BORROWER_TYPE_LABELS = {
  staff: "Staff",
  customer: "Customer",
  importer: "Importer",
  vendor: "Vendor",
  other: "Other",
}

const AccountLoanView = ({ modal, toggleModal, loan, loading, canAddRepayment = false, onAddRepayment }) => {
  const borrowerName = loan?.borrower_label || getEntityLabel(loan?.borrower, loan?.borrower_id, "Borrower")
  const borrowerTypeLabel = BORROWER_TYPE_LABELS[loan?.borrower_type] || null
  const statusName = getEntityLabel(loan?.loan_status || loan?.loanStatus, loan?.loan_status_id, "Status")
  const accountName = getEntityLabel(loan?.account, loan?.account_id, "Account")
  const createdBy = getUserLabel(loan?.createdBy || loan?.created_by)
  const updatedBy = getUserLabel(loan?.updatedBy || loan?.updated_by)
  const remaining = loan?.remaining
  const repayments = loan?.repayments || []

  const statusColor =
    statusName?.toLowerCase?.() === "active" ? "success" :
    statusName?.toLowerCase?.() === "closed" ? "secondary" :
    statusName?.toLowerCase?.() === "overdue" ? "danger" : "warning"

  return (
    <ThemeModal isOpen={modal} toggle={toggleModal} title="Loan Details" size="xl" modalBodyClass="bg-body-tertiary">
      {loading ? (
        <div className="text-center py-4"><Spinner size="sm" /></div>
      ) : !loan ? (
        <div className="text-center py-4 text-muted">No details available.</div>
      ) : (
        <div className="mx-auto" style={{ maxWidth: 740 }}>
          {loan.is_reversed && (
            <div className="alert alert-warning d-flex align-items-center gap-2 mb-3 py-2 px-3 rounded-3" style={{ fontSize: 13 }}>
              <i className="bx bx-info-circle fs-5" />
              <span>This loan has been <strong>reversed</strong>. The disbursement has been cancelled by a ledger reversal entry.</span>
            </div>
          )}
          <div className="rounded-4 border overflow-hidden" style={{ background: "linear-gradient(180deg, #ffffff 0%, #fbfdff 100%)", borderColor: "rgba(15, 23, 42, 0.08)", boxShadow: "0 24px 64px rgba(15, 23, 42, 0.08)" }}>
            <div className="px-4 py-2 d-flex align-items-center justify-content-between" style={{ background: "linear-gradient(90deg, rgba(15, 23, 42, 0.96) 0%, rgba(30, 41, 59, 0.96) 100%)" }}>
              <div className="text-white text-uppercase fw-semibold" style={{ fontSize: 10, letterSpacing: 1.6 }}>Loan Record</div>
              <div className="text-white-50 font-monospace" style={{ fontSize: 12 }}>ALN-{String(loan.id ?? "0000").padStart(4, "0")}</div>
            </div>

            <div className="px-4 py-3 border-bottom" style={{ background: "radial-gradient(circle at top left, rgba(99, 102, 241, 0.12), transparent 34%), linear-gradient(180deg, #f8fbfd 0%, #ffffff 100%)", borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
                <div>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Borrower</div>
                  <div className="h5 mb-1 text-dark" style={{ letterSpacing: -0.2 }}>{borrowerName}</div>
                  <div className="d-flex flex-wrap gap-2">
                    {borrowerTypeLabel && <Badge color="light" pill className="px-2 py-1 text-dark">{borrowerTypeLabel}</Badge>}
                    <Badge color={statusColor} pill className="px-2 py-1">{statusName}</Badge>
                    {loan.is_reversed && <Badge color="secondary" pill className="px-2 py-1">Reversed</Badge>}
                    <span className="text-muted small font-monospace">#{loan.id ?? "-"}</span>
                    <span className="text-muted small">{loan.loan_date ? formatReadableDate(loan.loan_date) : "No date"}</span>
                  </div>
                </div>
                <div className="text-md-end">
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Loan Amount</div>
                  <div className="fw-bold text-dark" style={{ fontSize: 34, lineHeight: 0.95, letterSpacing: -1 }}>{getMoneyDisplayValue(loan.amount)}</div>
                  <div className="text-muted small mt-1">from {accountName}</div>
                </div>
              </div>
            </div>

            <div className="position-relative" style={{ height: 18, background: "#fff" }}>
              <div style={{ position: "absolute", left: 0, right: 0, top: "50%", borderTop: "1px dashed rgba(15, 23, 42, 0.18)" }} />
              <div className="position-absolute rounded-circle bg-body-tertiary" style={{ width: 22, height: 22, left: -11, top: -2 }} />
              <div className="position-absolute rounded-circle bg-body-tertiary" style={{ width: 22, height: 22, right: -11, top: -2 }} />
            </div>

            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <Row className="g-3 g-md-4">
                <Col md={6}>
                  <ReceiptSection title="Loan Details">
                    <ReceiptRow label="Borrower" value={borrowerName} />
                    <ReceiptRow label="Borrower Type" value={borrowerTypeLabel} />
                    <ReceiptRow label="Loan Amount" value={getMoneyDisplayValue(loan.amount)} mono />
                    <ReceiptRow label="Loan Date" value={loan.loan_date ? formatReadableDate(loan.loan_date) : "-"} />
                    <ReceiptRow label="Status" value={statusName} />
                  </ReceiptSection>
                </Col>
                <Col md={6}>
                  <ReceiptSection title="Account & Purpose">
                    <ReceiptRow label="Account" value={accountName} />
                    <ReceiptRow label="Purpose" value={loan.purpose || "Not specified"} />
                    <ReceiptRow label="Record ID" value={loan.id ?? "-"} mono />
                    <ReceiptRow label="Loan Status" value={statusName} />
                  </ReceiptSection>
                </Col>
              </Row>
            </div>

            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <ReceiptSection title="Loan Summary">
                <div className="rounded-3 px-3 py-2" style={{ background: "rgba(248, 250, 252, 0.9)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                  <ReceiptRow label="Loan Amount" value={getMoneyDisplayValue(loan.amount)} mono />
                  <ReceiptRow label="Remaining Balance" value={remaining !== undefined ? getMoneyDisplayValue(remaining) : "-"} mono strong />
                  <ReceiptRow label="Source Account" value={accountName} />
                  <ReceiptRow label="Status" value={statusName} />
                  <ReceiptRow label="Ledger Status" value={loan.is_reversed ? "⚠ Reversed — Cancelled by reversal entry" : "Active"} />
                </div>
              </ReceiptSection>
            </div>

            <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
              <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                <ReceiptSection title="Repayment History">
                  <div className="text-muted small">See repayment progress without switching to another page.</div>
                </ReceiptSection>
                {canAddRepayment && typeof onAddRepayment === "function" && (
                  <Button color="success" size="sm" onClick={() => onAddRepayment(loan)}>
                    <i className="mdi mdi-cash-plus me-1" />
                    Add Repayment
                  </Button>
                )}
              </div>
              {repayments.length ? (
                <div className="table-responsive">
                  <Table size="sm" className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Method</th>
                        <th>Account</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {repayments.map(item => (
                        <tr key={item.id}>
                          <td>{item.repayment_date ? formatReadableDate(item.repayment_date) : "-"}</td>
                          <td>{item.payment_method?.name || item.paymentMethod?.name || "-"}</td>
                          <td>{item.account?.name || "-"}</td>
                          <td className="text-end fw-semibold">{getMoneyDisplayValue(item.amount, loan?.account?.currency || "BDT")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-muted small">No repayments recorded yet.</div>
              )}
            </div>

            {loan.notes && (
              <div className="px-4 py-3 border-bottom bg-white" style={{ borderColor: "rgba(15, 23, 42, 0.08)" }}>
                <ReceiptSection title="Notes">
                  <div className="text-dark rounded-3 px-3 py-2" style={{ whiteSpace: "pre-wrap", lineHeight: 1.5, background: "linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, rgba(255, 255, 255, 1) 100%)", border: "1px solid rgba(15, 23, 42, 0.06)" }}>
                    {loan.notes}
                  </div>
                </ReceiptSection>
              </div>
            )}

            <div className="px-4 py-3" style={{ background: "linear-gradient(180deg, rgba(248, 250, 252, 0.75) 0%, rgba(241, 245, 249, 0.9) 100%)" }}>
              <Row className="g-3">
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Created By</div>
                  <div className="small text-dark">{createdBy}</div>
                  <div className="small text-muted">{loan.created_at ? formatReadableDate(loan.created_at) : "-"}</div>
                </Col>
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Updated By</div>
                  <div className="small text-dark">{updatedBy}</div>
                  <div className="small text-muted">{loan.updated_at ? formatReadableDate(loan.updated_at) : "-"}</div>
                </Col>
                <Col md={4}>
                  <div className="text-uppercase text-muted fw-semibold mb-1" style={{ fontSize: 10, letterSpacing: 1.2 }}>Record Status</div>
                  <div className="small text-dark fw-semibold">{loan.deleted_at ? "Archived" : "Active"}</div>
                  <div className="small text-muted">{loan.deleted_at ? formatReadableDate(loan.deleted_at) : "No deletion recorded"}</div>
                </Col>
              </Row>
            </div>

          </div>
        </div>
      )}
    </ThemeModal>
  )
}

export default AccountLoanView
